import { useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import type { Transaction } from '../api/types';
import { TransactionRow } from '../components/TransactionRow';
import { childCategories, paymentMethods, topCategories } from '../lib/catalog';
import { useApp } from '../state';
import { ErrorBox } from './Dashboard';

interface Page {
  rows: Transaction[];
  nextCursor: string | null;
}

/** 기간·종류·분류·수단·검색어로 서버에서 조회한다. 필터에는 삭제(보관)된 분류도 나온다. */
export function HistoryPage() {
  const { period, catalog, historyFilter: filter, setHistoryFilter: setFilter, dataVersion } = useApp();
  const [q, setQ] = useState(filter.q ?? '');
  const [page, setPage] = useState<Page | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const generation = useRef(0);
  const latestFilter = useRef(filter);
  latestFilter.current = filter;
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    const handle = setTimeout(() => setFilter({ ...latestFilter.current, q: q.trim() || undefined }), 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const query = (cursor?: string) => ({
    from: period.start,
    to: period.end,
    kind: filter.kind || undefined,
    categoryId: filter.subcategoryId || filter.categoryId,
    paymentMethodId: filter.paymentMethodId,
    q: filter.q,
    limit: 50,
    cursor
  });

  useEffect(() => {
    const current = ++generation.current;
    setPage(null);
    setLoading(true);
    setError(null);
    api.get<{ data: Transaction[]; nextCursor: string | null }>('/transactions', query()).then(
      (res) => current === generation.current && (setPage({ rows: res.data, nextCursor: res.nextCursor }), setLoading(false)),
      (e: Error) => current === generation.current && (setError(e.message), setLoading(false))
    );
    return () => {
      generation.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period.start, period.end, filter.kind, filter.categoryId, filter.subcategoryId, filter.paymentMethodId, filter.q, dataVersion, retry]);

  async function more() {
    if (!page?.nextCursor || loading) return;
    const current = generation.current;
    setLoading(true);
    try {
      const res = await api.get<{ data: Transaction[]; nextCursor: string | null }>('/transactions', query(page.nextCursor));
      if (current !== generation.current) return;
      setPage({ rows: [...page.rows, ...res.data], nextCursor: res.nextCursor });
    } catch (e) {
      if (current === generation.current) setError((e as Error).message);
    } finally {
      if (current === generation.current) setLoading(false);
    }
  }

  const catalogKind = filter.kind === 'income' ? 'income' : filter.kind === 'expense' || filter.kind === 'refund' ? 'expense' : undefined;
  const tops = topCategories(catalog, catalogKind, true);
  const subs = childCategories(catalog, filter.categoryId, true);
  const payments = paymentMethods(catalog, catalogKind, true);
  const label = (name: string, active: boolean) => `${name}${active ? '' : ' (삭제됨)'}`;

  return (
    <article className="card">
      <div className="filters">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="수입원·사용처·메모 검색" aria-label="내역 검색" maxLength={100} />
        <select value={filter.kind ?? ''} onChange={(e) => setFilter({ ...filter, kind: e.target.value as never, categoryId: undefined, subcategoryId: undefined, paymentMethodId: undefined })} aria-label="거래 종류">
          <option value="">전체 종류</option>
          <option value="income">수입</option>
          <option value="expense">지출</option>
          <option value="refund">환불</option>
          <option value="transfer">이체</option>
        </select>
        <select value={filter.categoryId ?? ''} onChange={(e) => setFilter({ ...filter, categoryId: e.target.value || undefined, subcategoryId: undefined })} aria-label="대분류">
          <option value="">전체 대분류</option>
          {tops.map((c) => (
            <option key={c.id} value={c.id}>
              {catalogKind ? '' : c.kind === 'income' ? '수입 · ' : '지출 · '}
              {label(c.name, c.active)}
            </option>
          ))}
        </select>
        <select value={filter.subcategoryId ?? ''} onChange={(e) => setFilter({ ...filter, subcategoryId: e.target.value || undefined })} disabled={!subs.length} aria-label="소분류">
          <option value="">전체 소분류</option>
          {subs.map((c) => (
            <option key={c.id} value={c.id}>
              {label(c.name, c.active)}
            </option>
          ))}
        </select>
        <select value={filter.paymentMethodId ?? ''} onChange={(e) => setFilter({ ...filter, paymentMethodId: e.target.value || undefined })} aria-label="수단">
          <option value="">전체 수단</option>
          {payments.map((p) => (
            <option key={p.id} value={p.id}>
              {catalogKind ? '' : p.kind === 'income' ? '입금 · ' : '결제 · '}
              {label(p.name, p.active)}
            </option>
          ))}
        </select>
      </div>
      {error && !page ? (
        <ErrorBox message={error} retry={() => setRetry((r) => r + 1)} />
      ) : !page ? (
        <div className="empty">불러오는 중…</div>
      ) : page.rows.length ? (
        page.rows.map((tx) => <TransactionRow key={tx.id} tx={tx} actions />)
      ) : (
        <div className="empty">조건에 맞는 내역이 없어요.</div>
      )}
      {error && page && <p className="error">{error}</p>}
      {page?.nextCursor && (
        <button className="full more" onClick={() => void more()} disabled={loading}>
          {loading ? '불러오는 중…' : '더 보기'}
        </button>
      )}
      <p className="hint">삭제는 기록을 숨기는 논리 삭제입니다. 다른 기기에서 먼저 바꾼 기록은 덮어쓰지 않고 두 값을 보여드려요.</p>
    </article>
  );
}
